/**
 * Canonical analyte name mapping.
 *
 * The AI lab parser (Claude) produces inconsistent marker names across
 * different PDF reports. The same biomarker can appear as "Vitamin D",
 * "Vitamin D, 25-OH Total", "25-OH Vitamin D", etc. Without normalization,
 * these create duplicate records in the `labs` table and cause the web
 * and mobile app to show different "latest" values.
 *
 * This map normalizes all known variants to a single canonical name at
 * ingest time (parse-labs.js + Labs.tsx save handler). The canonical
 * name is what gets stored in the DB and displayed everywhere.
 *
 * To add a new alias: add the lowercase variant as a key, canonical name as value.
 * The lookup is case-insensitive (input is lowercased before matching).
 */

const ANALYTE_ALIASES: Record<string, string> = {
  // ── Vitamin D ─────────────────────────────────────────────────────
  'vitamin d':                    'Vitamin D',
  'vitamin d, 25-oh total':      'Vitamin D',
  'vitamin d, 25-oh':            'Vitamin D',
  'vitamin d 25-oh total':       'Vitamin D',
  'vitamin d 25-hydroxy':        'Vitamin D',
  'vitamin d, 25-hydroxy':       'Vitamin D',
  '25-oh vitamin d':             'Vitamin D',
  '25-oh vitamin d, total':      'Vitamin D',
  '25-hydroxyvitamin d':         'Vitamin D',
  '25-hydroxyvitamin d, total':  'Vitamin D',
  '25-hydroxy vitamin d':        'Vitamin D',
  '25-hydroxy vitamin d, total': 'Vitamin D',
  'vitamin d, 25-oh, total':     'Vitamin D',
  'vitamin d, total':            'Vitamin D',
  'vitamin d total':             'Vitamin D',
  'vitamin d3':                  'Vitamin D',
  '25(oh)d':                     'Vitamin D',
  '25(oh)d, total':              'Vitamin D',
  'd, 25-hydroxy, total':        'Vitamin D',

  // ── hsCRP / CRP ──────────────────────────────────────────────────
  // All CRP variants map to hsCRP — this app tracks inflammation for
  // mental health, and hsCRP is the clinically relevant marker.
  'hscrp':                        'hsCRP',
  'hs crp':                       'hsCRP',
  'hs-crp':                       'hsCRP',
  'high sensitivity crp':         'hsCRP',
  'high sensitivity c-reactive protein': 'hsCRP',
  'c-reactive protein, high sensitivity': 'hsCRP',
  'crp, high sensitivity':        'hsCRP',
  'c-reactive protein':           'hsCRP',
  'crp':                          'hsCRP',
  'c reactive protein':           'hsCRP',

  // ── B12 ───────────────────────────────────────────────────────────
  'b12':                          'Vitamin B12',
  'b-12':                         'Vitamin B12',
  'vitamin b12':                  'Vitamin B12',
  'vitamin b-12':                 'Vitamin B12',
  'vitamin b 12':                 'Vitamin B12',
  'cobalamin':                    'Vitamin B12',
  'cyanocobalamin':               'Vitamin B12',
  'b12, serum':                   'Vitamin B12',
  'vitamin b12, serum':           'Vitamin B12',

  // ── Folate ────────────────────────────────────────────────────────
  'folate':                       'Folate',
  'folic acid':                   'Folate',
  'folate, serum':                'Folate',
  'folate (folic acid)':          'Folate',
  'folate, rbc':                  'RBC Folate',
  'rbc folate':                   'RBC Folate',

  // ── Magnesium ─────────────────────────────────────────────────────
  'magnesium':                    'Magnesium',
  'magnesium, serum':             'Magnesium',
  'magnesium serum':              'Magnesium',
  'mag':                          'Magnesium',
  'mg':                           'Magnesium',
  'rbc magnesium':                'RBC Magnesium',
  'magnesium, rbc':               'RBC Magnesium',

  // ── Ferritin ──────────────────────────────────────────────────────
  'ferritin':                     'Ferritin',
  'ferritin, serum':              'Ferritin',
  'ferritin serum':               'Ferritin',
  'serum ferritin':               'Ferritin',

  // ── Iron panel ────────────────────────────────────────────────────
  'iron':                         'Iron',
  'iron, serum':                  'Iron',
  'iron, total':                  'Iron',
  'tibc':                         'TIBC',
  'total iron binding capacity':  'TIBC',
  'iron binding capacity, total': 'TIBC',

  // ── Thyroid ───────────────────────────────────────────────────────
  'tsh':                          'TSH',
  'thyroid stimulating hormone':  'TSH',
  'free t3':                      'Free T3',
  'free t4':                      'Free T4',
  'triiodothyronine, free':       'Free T3',
  'thyroxine, free':              'Free T4',

  // ── Glucose / HbA1c ──────────────────────────────────────────────
  'glucose':                      'Glucose',
  'glucose, fasting':             'Glucose',
  'fasting glucose':              'Glucose',
  'glucose, serum':               'Glucose',
  'hba1c':                        'HbA1c',
  'hemoglobin a1c':               'HbA1c',
  'a1c':                          'HbA1c',

  // ── Insulin ───────────────────────────────────────────────────────
  'insulin':                      'Insulin',
  'insulin, fasting':             'Insulin',
  'fasting insulin':              'Insulin',

  // ── Lipids ────────────────────────────────────────────────────────
  'total cholesterol':            'Total Cholesterol',
  'cholesterol, total':           'Total Cholesterol',
  'ldl':                          'LDL',
  'ldl cholesterol':              'LDL',
  'ldl-c':                        'LDL',
  'ldl cholesterol, calculated':  'LDL',
  'hdl':                          'HDL',
  'hdl cholesterol':              'HDL',
  'hdl-c':                        'HDL',
  'triglycerides':                'Triglycerides',
  'triglyceride':                 'Triglycerides',
  'apob':                         'ApoB',
  'apolipoprotein b':             'ApoB',
  'lp(a)':                        'Lp(a)',
  'lipoprotein(a)':               'Lp(a)',
  'lipoprotein (a)':              'Lp(a)',

  // ── Homocysteine ──────────────────────────────────────────────────
  'homocysteine':                 'Homocysteine',
  'homocysteine, plasma':         'Homocysteine',

  // ── Cortisol ──────────────────────────────────────────────────────
  'cortisol':                     'Cortisol',
  'cortisol, am':                 'Cortisol',
  'cortisol, morning':            'Cortisol',

  // ── PSA ───────────────────────────────────────────────────────────
  'psa':                          'PSA',
  'psa, total':                   'PSA',
  'prostate specific antigen':    'PSA',

  // ── Testosterone ──────────────────────────────────────────────────
  'testosterone':                 'Testosterone',
  'testosterone, total':          'Testosterone',
  'free testosterone':            'Free Testosterone',
  'testosterone, free':           'Free Testosterone',

  // ── Omega-3 ───────────────────────────────────────────────────────
  'omega-3 index':                'Omega-3 Index',
  'omega 3 index':                'Omega-3 Index',
  'omega-3':                      'Omega-3 Index',
  'omega 3':                      'Omega-3 Index',
  'omega-3 index, whole blood':   'Omega-3 Index',
  'omegacheck':                   'Omega-3 Index',

  // ── Uric Acid ─────────────────────────────────────────────────────
  'uric acid':                    'Uric Acid',
  'urate':                        'Uric Acid',
};

/**
 * Normalize a marker name to its canonical form.
 * Returns the canonical name if an alias exists, otherwise returns the
 * original marker name with leading/trailing whitespace trimmed.
 */
export function normalizeMarker(marker: string): string {
  const trimmed = marker.trim();
  const canonical = ANALYTE_ALIASES[trimmed.toLowerCase()];
  return canonical ?? trimmed;
}

